
import React, { useState } from 'react';
import { Table, TableShape, CategoryDefinition, Reservation } from '../types';
import { Clock, Users } from 'lucide-react';

interface TableNodeProps {
  table: Table;
  categories?: CategoryDefinition[]; 
  isSelected?: boolean;
  isOccupied?: boolean;
  isUpcoming?: boolean;
  isHighlighted?: boolean;
  isSimple?: boolean;
  reservations?: Reservation[];
  onMouseDown?: (e: React.MouseEvent, tableId: string) => void;
  onResizeStart?: (e: React.MouseEvent, tableId: string) => void;
  onTableClick?: (tableId: string) => void;
  scale?: number;
}

export const TableNode: React.FC<TableNodeProps> = ({ 
  table, 
  categories = [],
  isSelected, 
  isOccupied,
  isUpcoming,
  isHighlighted,
  isSimple = false,
  reservations = [],
  onMouseDown, 
  onResizeStart,
  onTableClick,
  scale = 1 
}) => {
  const [isHovered, setIsHovered] = useState(false);
  
  const categoryDef = categories.find(c => c.id === table.category);
  
  const width = table.width || 80;
  const height = table.height || 60;

  const showTooltipAtBottom = table.y < 140;

  const getEndTime = (startTime: string, duration: number) => {
    try {
      const [hours, minutes] = startTime.split(':').map(Number);
      const date = new Date();
      date.setHours(hours);
      date.setMinutes(minutes + duration);
      return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    } catch (e) {
      return "--:--";
    }
  };

  const styles: React.CSSProperties = {
    position: 'absolute',
    left: table.x,
    top: table.y,
    width: width,
    height: height,
    transform: `rotate(${table.rotation}deg) scale(${scale})`,
    cursor: onMouseDown ? 'move' : 'pointer',
    zIndex: isHovered ? 100 : 10,
    transition: onMouseDown ? 'none' : 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    willChange: onMouseDown ? 'left, top' : 'auto',
    background: 'transparent',
    border: 'none',
    boxShadow: 'none',
  };

  const getColors = () => {
    let bgColor = categoryDef?.color || '#ecfdf5';
    let borderColor = isSelected ? '#3b82f6' : (isHighlighted ? '#10b981' : '#065f4633');
    
    if (isSimple) {
      borderColor = isSelected ? '#3b82f6' : '#94a3b8';
      bgColor = isSelected ? '#f8fafc' : (isHighlighted ? '#f0f9ff' : '#ffffff');
    }

    if (isSelected) bgColor = isSimple ? '#f8fafc' : '#dbeafe'; 
    if (isHighlighted) bgColor = isSimple ? '#f0f9ff' : '#d1fae5';

    return { bgColor, borderColor };
  };

  const { bgColor, borderColor } = getColors();

  const getCategoryIcon = () => {
    const iconType = categoryDef?.icon || '⚽';
    return <span className="text-lg leading-none">{iconType}</span>;
  };

  const renderShape = () => {
    const borderWeight = isSelected ? 3 : (isSimple ? 1 : 2);
    const dropShadow = (isSelected || isSimple) 
      ? (isSimple ? 'none' : 'drop-shadow(0 0 4px rgba(59, 130, 246, 0.5))')
      : 'drop-shadow(0 2px 3px rgba(0,0,0,0.1))';

    if (table.shape === TableShape.TRIANGLE) {
      return (
        <div className="w-full h-full relative flex items-center justify-center overflow-visible">
          <svg 
            className="absolute inset-0 w-full h-full overflow-visible" 
            viewBox="0 0 100 100" 
            preserveAspectRatio="none"
            style={{ filter: dropShadow }}
          >
            <polygon 
              points="50,5 98,95 2,95" 
              fill={bgColor} 
              stroke={borderColor} 
              strokeWidth={borderWeight}
              vectorEffect="non-scaling-stroke"
              className="transition-all duration-200"
            />
          </svg>
          {/* Content positioned towards the base of the triangle for better readability */}
          <div className="z-10 flex flex-col items-center justify-end h-full pb-3 pointer-events-none w-full px-2">
            <div className="mb-1">{getCategoryIcon()}</div>
            <span className={`font-black text-[9px] uppercase tracking-tighter truncate max-w-full leading-none ${isSimple ? 'text-slate-700' : 'text-emerald-950'}`}>
              {table.name}
            </span>
            <span className={`text-[6px] font-black mt-0.5 ${isSimple ? 'text-slate-400' : 'text-emerald-800/60'}`}>{table.seats} P.</span>
          </div>
        </div>
      );
    }

    const borderRadius = table.shape === TableShape.CIRCLE ? '9999px' : (isSimple ? '4px' : '6px');
    
    return (
      <div 
        className={`w-full h-full flex flex-col items-center justify-center relative transition-all duration-200 ${isSimple ? 'shadow-none' : ''}`}
        style={{ 
          backgroundColor: bgColor,
          border: `${borderWeight}px solid ${borderColor}`,
          borderRadius: borderRadius,
          filter: dropShadow
        }}
      >
        <div className={`absolute inset-1 pointer-events-none ${isSimple ? 'hidden' : 'border border-white/20'}`} style={{ borderRadius: table.shape === TableShape.CIRCLE ? '9999px' : '4px' }}></div>
        <div className="z-10 flex flex-col items-center pointer-events-none">
            {getCategoryIcon()}
            <span className={`font-bold text-[9px] mt-0.5 uppercase tracking-tight truncate max-w-full px-1 ${isSimple ? 'text-slate-800' : 'text-emerald-900'}`}>{table.name}</span>
            <span className={`text-[7px] font-black ${isSimple ? 'text-slate-500' : 'text-emerald-800/70'}`}>{table.seats} PERS.</span>
        </div>
      </div>
    );
  };

  return (
    <div
      style={styles}
      onMouseDown={(e) => onMouseDown?.(e, table.id)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={(e) => {
        e.stopPropagation();
        if (!onMouseDown) {
          onTableClick?.(table.id);
        }
      }}
      className="select-none"
    >
      {renderShape()}

      {isHovered && !onMouseDown && (
        <div 
          className={`absolute ${showTooltipAtBottom ? 'top-full mt-3' : 'bottom-full mb-3'} left-1/2 -translate-x-1/2 w-56 bg-slate-900/95 backdrop-blur-md text-white p-3 rounded-xl shadow-2xl border border-white/10 z-[110] animate-fade-in-up pointer-events-none`}
          style={{ transform: `translateX(-50%) rotate(${-table.rotation}deg)` }}
        >
          <div className="text-[10px] font-black uppercase tracking-widest text-emerald-400 mb-2 border-b border-white/10 pb-1">
            Heute geplant
          </div>
          {reservations.length === 0 ? (
            <div className="text-[10px] text-slate-400 italic">Keine Spiele heute</div>
          ) : (
            <div className="space-y-2">
              {reservations.sort((a,b) => a.time.localeCompare(b.time)).map(res => (
                <div key={res.id} className="flex flex-col gap-0.5">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-black flex items-center gap-1">
                      <Clock size={10} className="text-emerald-500" /> 
                      {res.time} - {getEndTime(res.time, res.durationMinutes)}
                    </span>
                    <span className="text-[9px] bg-white/10 px-1 rounded flex items-center gap-0.5">
                      <Users size={8} /> {res.partySize}
                    </span>
                  </div>
                  <div className="text-[10px] font-medium text-slate-300 truncate pl-3 border-l border-emerald-500/30 ml-1">
                    {res.customerName}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className={`absolute ${showTooltipAtBottom ? 'bottom-full border-b-slate-900/95' : 'top-full border-t-slate-900/95'} left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent ${showTooltipAtBottom ? 'border-b-[6px]' : 'border-t-[6px]'}`}></div>
        </div>
      )}
      
      {isSelected && onResizeStart && (
          <div
            className="absolute bottom-[-6px] right-[-6px] w-4 h-4 bg-blue-500 cursor-nwse-resize z-50 rounded-full border-2 border-white shadow-md hover:scale-125 transition-transform"
            onMouseDown={(e) => { e.stopPropagation(); onResizeStart(e, table.id); }}
          />
      )}
    </div>
  );
};
